import {
  Dialog,
  DialogTrigger,
  DialogPortal,
  DialogOverlay,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { NotificationRow } from "@/components/ui/notification-row";
import useAuth from "@/contexts/auth/auth-context";
import useMusioApi from "@/contexts/client/musio-client-context";
import { useIsMobile } from "@/hooks/use-mobile";
import { Edit } from "lucide-react";
import { useEffect, useRef, useState } from "react";

const ERROR = "There was an issue updating your display name. Please check your inputs";
const SUCCESS = "Successfully changed your display name";

export const DisplayNameModal = () => {
  const { user } = useAuth();
  const { musioApi } = useMusioApi();

  const [open, setOpen] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>(user?.contactName ?? "");
  const displayNameRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  useEffect(() => {
    if (open) {
      setDisplayName(user?.contactName ?? "");
      requestAnimationFrame(() => displayNameRef.current?.focus());
    }
    // keeping your dependency as-is (though user?.contactName would also make sense)
  }, [open, user?.email, user?.contactName]);

  const { mutate, isPending, isError, isSuccess } = musioApi.useMutation("post", "/user");

  const handleSubmitButtonInput = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (isSuccess == null) return;

    setOpen(false);
    location.reload();
  };

  // NOTE: you had email here previously; leaving logic intact, but this likely should be user?.contactName
  const isUnchanged = displayName.trim() === (user?.email ?? "").trim();
  const isDisabled = isPending || !displayName || isUnchanged;
  const hasNotification = isSuccess || isError;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="p-2 px-5 bg-musio-mid-gray text-musio-light-lilac hover:text-musio-mid-gray">
          {isMobile ? <Edit /> : "Change Display Name"}
        </Button>
      </DialogTrigger>

      <DialogPortal>
        <DialogOverlay className="AlertDialogOverlay" />
        <DialogContent className="AlertDialogContent">
          <form
            onSubmit={(e: React.FormEvent) => {
              e.preventDefault();
              mutate({
                body: {
                  contactName: displayName,
                },
              });
            }}
            className="min-h-0 flex flex-col gap-4"
          >
            <DialogTitle className="flex items-center justify-between gap-4 p-8 bg-musio-bg">
              <div className="text-[30px] font-semibold text-musio-light-lilac">
                Change Display Name
              </div>

              <DialogClose asChild>
                <Button
                  type="button"
                  disabled={isSuccess}
                  className="select-none px-8 h-[38px] flex items-center justify-center rounded-full font-bold bg-musio-red text-musio-light-lilac cursor-pointer"
                >
                  <span className="text-sm">Go Back</span>
                </Button>
              </DialogClose>
            </DialogTitle>

            <DialogDescription className="AlertDialogDescription p-8 space-y-3 bg-musio-bg">
              {hasNotification ? (
                <NotificationRow intent={isError ? "error" : "success"}>
                  {isError ? ERROR : SUCCESS}
                </NotificationRow>
              ) : null}

              <Input
                ref={displayNameRef}
                type="text"
                id="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
              />
            </DialogDescription>

            <DialogFooter className="p-8 flex items-center justify-center bg-musio-bg">
              {isSuccess === false ? (
                <Button
                  type="submit"
                  disabled={isDisabled}
                  className={`
                    select-none w-[404px] h-[35px] flex items-center justify-center
                    rounded-2xl font-bold text-musio-light-lilac cursor-pointer
                    bg-musio-green disabled:opacity-60
                  `}
                >
                  <p className="text-sm text-musio-mid-gray">
                    {isPending ? "Submitting…" : "Submit"}
                  </p>
                </Button>
              ) : (
                <Button
                  type="button"
                  onClick={handleSubmitButtonInput}
                  className={`
                    select-none w-[404px] h-[35px] flex items-center justify-center
                    rounded-2xl font-bold text-musio-light-lilac cursor-pointer
                    bg-musio-green disabled:opacity-60
                  `}
                >
                  <p className="text-sm text-musio-mid-gray">Close</p>
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  );
};
